import React from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { colors, shadows, radius, spacing, typography, transitions } from "../utils/designTokens";

/**
 * Tarjeta de KPI clickeable para dashboards.
 *
 * Props:
 * - icon (ReactElement): icono del KPI
 * - iconBg (string): color de fondo del icono
 * - iconColor (string, opcional): color del icono (default: primary)
 * - label (string): nombre del KPI
 * - value (string | number): valor principal
 * - trend ({ direction: "up" | "down" | "neutral", value: string, label?: string }, opcional)
 * - onClick (function, opcional): navegación al hacer click
 * - badge (string, opcional): texto de badge (ej: "78%")
 * - badgeColor ("success" | "warning" | "error", opcional): color del badge
 */
const KPICard = ({
  icon: Icon,
  iconBg = colors.primaryLighter,
  iconColor = colors.primary,
  label,
  value,
  trend,
  onClick,
  badge,
  badgeColor = "success",
}) => {
  const isClickable = !!onClick;

  const badgeColors = {
    success: { bg: colors.successLight, text: colors.successDark },
    warning: { bg: colors.warningLight, text: colors.warningDark },
    error: { bg: colors.errorLight, text: colors.errorDark },
  };

  const bc = badgeColors[badgeColor] || badgeColors.success;

  return (
    <div
      style={{
        ...styles.card,
        cursor: isClickable ? "pointer" : "default",
      }}
      className={isClickable ? "habitia-card-hover" : undefined}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `${label}: ${value}. Click para ver detalle` : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div style={styles.header}>
        <div style={{ ...styles.iconWrap, backgroundColor: iconBg }}>
          {Icon && <Icon style={{ fontSize: 18, color: iconColor }} />}
        </div>
        {badge && (
          <span
            style={{
              ...styles.badge,
              backgroundColor: bc.bg,
              color: bc.text,
            }}
          >
            {badge}
          </span>
        )}
      </div>

      <div style={styles.valueWrap}>
        <span style={styles.value}>{value}</span>
      </div>

      <span style={styles.label}>{label}</span>

      {trend && (
        <div style={styles.trendWrap}>
          {trend.direction === "up" && (
            <FaArrowUp style={{ fontSize: 10, color: colors.success }} />
          )}
          {trend.direction === "down" && (
            <FaArrowDown style={{ fontSize: 10, color: colors.error }} />
          )}
          <span
            style={{
              ...styles.trendText,
              color: trend.direction === "up" ? colors.success : trend.direction === "down" ? colors.error : colors.gray600,
            }}
          >
            {trend.value}
          </span>
          {trend.label && (
            <span style={styles.trendLabel}>{trend.label}</span>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: colors.bgCard,
    borderRadius: radius.md,
    padding: spacing[5],
    boxShadow: shadows.sm,
    transition: `all ${transitions.normal}`,
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: radius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  badge: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    padding: "4px 10px",
    borderRadius: radius.sm,
    lineHeight: 1,
  },
  valueWrap: {
    marginTop: spacing[1],
  },
  value: {
    fontSize: typography.sizes["3xl"],
    fontWeight: 400,
    color: colors.black,
    lineHeight: 1.2,
  },
  label: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    fontWeight: 400,
  },
  trendWrap: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: spacing[1],
  },
  trendText: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  trendLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray400,
  },
};

export default KPICard;
